import { Location } from "@angular/common";
import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { AdminService, BaseComponent, environment, EventBusService, HelperService, ProductService, ProductSnapshot, UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-fender-product-detail",
  templateUrl: "fender-product-detail.component.html",
  standalone: false,
})
export class AdminFenderProductDetailComponent extends BaseComponent implements OnInit {
  public loading: boolean = false;
  public sku: string;
  public fenderGear: ProductSnapshot;
  public dataSources: Array<{ source: string; api: string; endpoint: string; data?: any; error?: any }> = [];
  public snapshotError: string;
  public regeneratingSnapshot: boolean = false;
  public usingFmicProductsApi: boolean = false;
  public dryRun: boolean = false;
  public dryRunFmic: boolean = false;
  public title: string;
  public subTitle: string;
  // Header attributes surfaced above the description. Populated in hydrateFromSnapshot.
  public headerSeries: string = "";
  public headerBodyType: string = "";
  public headerType: string = "";
  public headerProductNo: string = "";
  // product.id below 10000 is a real PreSonus Product row (see hydrateFromSnapshot).
  public isPresonusProductId: boolean = false;
  public allSpecs: Array<Record<string, any>> = [];
  public specsCollapsed: boolean = false;
  public dataSourcesCollapsed: boolean = false;
  public showAllSpecs: boolean = false;
  public rawSnapshotCollapsed: boolean = false;
  public imagesCollapsed: boolean = false;
  public imageRows: Array<{ label: string; urls: string[] }> = [];
  public highlights: Array<{ title: string; description: string; url: string }> = [];
  public highlightsCollapsed: boolean = false;
  public featureBullets: string = "";
  public fmicProductsApiEnabled: boolean = !!(environment as any).features?.useFmicProductsApi;
  // Per-locale description overrides authored here, stored in product_metadata keyed by productNo
  // (the PIM product-line id) so one description covers every SKU/colorway in the line.
  public localizedDescriptions: Record<string, string> = {};
  public localizedShortDescriptions: Record<string, string> = {};
  public metadataLoaded: boolean = false;

  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    public location: Location,
    private _eventbusService: EventBusService,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.sku = this._activatedRoute.snapshot.params["sku"];
    const dryRunParam = this._activatedRoute.snapshot.queryParams["dryRunFmic"];
    this.dryRunFmic = dryRunParam === "true" || dryRunParam === true;

    // Search results are a lean, unpersisted preview (see AdminFenderProductsComponent) —
    // this page is the one place that actually fetches-or-builds the real, persisted
    // snapshot (findOrCreateFenderProductSnapshot on the backend), so it always re-fetches
    // by sku rather than trusting anything handed to it via router state.
    this.load();
  }

  load() {
    if (!this.sku) return;
    this.loading = true;
    this.adminService.searchFenderGear(this.sku, undefined, this.dryRunFmic).subscribe({
      next: (result: any) => {
        this.dataSources = result.dataSources || [];
        this.usingFmicProductsApi = !!result.usingFmicProductsApi;
        this.fenderGear = Array.isArray(result.snapshot) ? result.snapshot[0] : result.snapshot;
        this.snapshotError = result.snapshotError || undefined;
        this.dryRun = !!result.dryRun;
        this.hydrateFromSnapshot();
        this.loading = false;
      },
      error: () => {
        this.appService.alert("No Fender gear found with that SKU.");
        this.loading = false;
      },
    });
  }

  private hydrateFromSnapshot() {
    this.allSpecs = [];
    this.title = undefined;
    this.subTitle = undefined;
    // A SKU search here can resolve to a real PreSonus product (findOrCreateFenderProductSnapshot
    // redirects those to their Product.id-keyed, PIM-merged snapshot instead of a Fender one —
    // see [[fmic-products-api]]). pimSpecs is set for both cases; the header boxes/spec table/
    // highlights below are brand-agnostic, but the title/subtitle rewrite is Fender-specific
    // (Series/Color are a guitar-naming convention) and would clobber a real Product's own
    // title, so it's gated on product.id being a Fender/FMIC-only SKU (>= 10000), not a real
    // Product row.
    const specs: any = this.fenderGear?.pimSpecs;
    const isFenderOnlySnapshot = Number(this.fenderGear?.product?.id) >= 10000;
    // id is string | number; `<` coerces a numeric string, same as the template comparison did.
    this.isPresonusProductId = (this.fenderGear?.product?.id as number) < 10000;
    if (isFenderOnlySnapshot && specs?.general?.Series) {
      this.title = specs.general.Series;
      if (specs.body?.["Body Shape"]) this.title += " " + specs.body["Body Shape"];
    }
    if (isFenderOnlySnapshot && specs?.general?.Color) {
      this.subTitle = specs.general.Color;
      if (specs.body?.["Body Finish"]) this.subTitle += ", " + specs.body["Body Finish"];
    }
    this.headerSeries = specs?.general?.Series || "";
    this.headerBodyType = specs?.body?.["Body Shape"] || "";
    // Series ≠ Type; `Type` on the snapshot comes from the FMIC `type` field
    // ("Audio Interface", "Guitar", …).
    this.headerType = typeof specs?.Type === "string" ? specs.Type : "";
    this.headerProductNo = specs?.productNo && specs.productNo !== "NONE" ? String(specs.productNo) : "";
    // Show every section the backend adapter collected. `buildSnapshotFromFenderProduct`
    // emits one entry per FMIC block that has a `categoryName`, and re-writes that value
    // under the legacy `Name` key. Anything else the adapter mixes into pimSpecs
    // (Type, sku, productNo, Highlights, FeatureBullets, …) is a scalar / array — filtering
    // by `Name` keeps only the real spec sections without hardcoding a category allowlist.
    if (specs) {
      for (const key in specs) {
        const section = specs[key];
        if (section && typeof section === "object" && !Array.isArray(section) && section.Name) {
          this.allSpecs.push(section);
        }
      }
    }
    this.imageRows = this.helperService.buildImageRows((this.fenderGear as any)?.images);
    this.highlights = Array.isArray(specs?.Highlights) ? specs.Highlights : [];
    this.featureBullets = typeof specs?.FeatureBullets === "string" ? specs.FeatureBullets : "";
    this.loadLocalizedDescriptions();
  }

  /**
   * Loads any existing per-locale description overrides for this product line (keyed by productNo)
   * so the localized-descriptions editor can render and merge them. Empty maps if none exist yet.
   */
  private loadLocalizedDescriptions() {
    this.localizedDescriptions = {};
    this.localizedShortDescriptions = {};
    this.metadataLoaded = false;
    if (!this.headerProductNo) return;
    this.adminService.get(`product/metadata/${this.headerProductNo}`).subscribe({
      next: (result: any) => {
        this.localizedDescriptions = result?.descriptions || {};
        this.localizedShortDescriptions = result?.shortDescriptions || {};
        this.metadataLoaded = true;
      },
      error: () => {
        this.metadataLoaded = true;
      },
    });
  }

  regenerateSnapshot() {
    if (!this.fenderGear?.product?.id) return;
    this.regeneratingSnapshot = true;
    this.productService.regenerateSnapshot(this.fenderGear.product.id).subscribe({
      next: () => {
        this.appService.alert("Snapshot regenerated successfully.");
        this.regeneratingSnapshot = false;
      },
      error: () => {
        this.appService.alert("Failed to regenerate snapshot.");
        this.regeneratingSnapshot = false;
      },
    });
  }

  showImage(url: string) {
    this._eventbusService.emit(this._eventbusService.types.viewFullscreenImage, url);
  }

  goBack() {
    this.helperService.goBack();
  }
}
